import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/products', label: 'Products' },
  { to: '/services', label: 'Services' },
];

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/9fk2b69m_Vimal%20Plastics%20Logo%20Hi%20Res%20without%20tagline.png';

const Logo = () => (
  <Link to="/" className="flex items-center flex-shrink-0" data-testid="navbar-logo">
    <img
      src={LOGO_URL}
      alt="Vimal Plastics"
      className="h-12 w-auto object-contain"
      style={{ mixBlendMode: 'multiply' }}
    />
  </Link>
);

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location.pathname]);

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
      style={{
        backgroundColor: scrolled ? 'rgba(250,249,246,0.96)' : 'rgba(250,249,246,0.9)',
        backdropFilter: 'blur(14px)',
        borderBottom: `1px solid ${scrolled ? 'rgba(11,27,61,0.08)' : 'transparent'}`,
        boxShadow: scrolled ? '0 2px 20px rgba(11,27,61,0.04)' : 'none',
      }}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Logo />
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="text-xs tracking-[0.18em] uppercase transition-colors duration-200"
                style={{ color: location.pathname === link.to ? '#D4AF37' : '#4A4F5A' }}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          <div className="hidden md:block">
            <Link
              to="/contact"
              className="px-6 py-3 text-xs tracking-[0.18em] uppercase text-white transition-opacity hover:opacity-80"
              style={{ backgroundColor: '#0B1B3D' }}
              data-testid="nav-contact-button"
            >
              Contact Us
            </Link>
          </div>
          <button
            className="md:hidden p-2"
            style={{ color: '#0B1B3D' }}
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div
          className="md:hidden border-t py-4"
          style={{ backgroundColor: '#FAF9F6', borderColor: 'rgba(11,27,61,0.08)' }}
          data-testid="mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-6 flex flex-col gap-1">
            {NAV_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="text-xs tracking-[0.18em] uppercase py-3 border-b"
                style={{
                  color: location.pathname === link.to ? '#D4AF37' : '#4A4F5A',
                  borderColor: 'rgba(11,27,61,0.06)',
                }}
                data-testid={`mobile-nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-3 px-6 py-3 text-xs tracking-[0.18em] uppercase text-white text-center"
              style={{ backgroundColor: '#0B1B3D' }}
              data-testid="mobile-contact-button"
            >
              Contact Us
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
